import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { createReadableClass, getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';

type DatetimePickerButtonProps = HTMLAttributes<HTMLDivElement> & {
  selected?: boolean;
  disabled?: boolean;
};

export const DatetimePickerButton = forwardRef<
  HTMLDivElement,
  DatetimePickerButtonProps
>(({ selected, disabled, children, ...forwardingProps }, forwardingRef) => {
  const { textColor, accentColor } = useSettings();

  return (
    <Button
      ref={forwardingRef}
      _selected={selected}
      _disabled={disabled}
      _accentColor={accentColor}
      _textColor={textColor}
      {...forwardingProps}
    >
      {children}
    </Button>
  );
});

const Button = styled.div.attrs<{
  _selected?: boolean;
  _disabled?: boolean;
  _accentColor: string;
  _textColor: string;
}>(({ _selected, _disabled }) => {
  const classNameIndex = (() => {
    if (_selected) {
      return 'selected';
    }

    if (_disabled) {
      return 'disabled';
    }

    return;
  })();

  return {
    className: createReadableClass({
      component: 'datetime-picker-button',
      index: classNameIndex
    })
  };
})`
  min-width: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  cursor: pointer;

  ${({ _selected, _disabled, _accentColor, _textColor }) => {
    if (_disabled) {
      return css`
        color: ${getColorWithAlpha(_textColor, 0.3)};
        background: ${getColorWithAlpha(_textColor, 0.02)};
        cursor: default;
      `;
    }

    if (_selected) {
      return css`
        color: ${getInvertedColor(_accentColor)};
        background: ${_accentColor};
      `;
    }

    return css`
      color: ${_textColor};
      background: ${getColorWithAlpha(_textColor, 0.05)};
      &:hover {
        transition: all 0.3s;
        background: ${getColorWithAlpha(_textColor, 0.1)};
      }
    `;
  }}
`;
